import {loadGovernedBiContext,type BiRpcClient,type GovernedBiContext} from '@/lib/governedBiRetrieval';
import { useMemo } from "react";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PREVIEW_BEHAVIOR_NAME } from "@/data/leaderPreviewData";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";
import {
  deriveConstellationStateFromPracticeSummaries,
  type BehaviorRow,
  type LeaderBehaviorRow,
  type PracticeSummaryRow,
} from "@/lib/livingConstellation/deriveConstellationStateFromPracticeSummaries";
import {
  loadPersistedConstellationState,
  type MinimalSupabaseClient,
} from "@/lib/livingConstellation/loadPersistedConstellationState";
import {
  type BehaviorConstellationState,
  type ConstellationEvidenceItem,
  type CoreDomain,
} from "@/lib/livingConstellation";
import { emitSchemaFallbackTelemetry } from "@/lib/schemaFallbackTelemetry";

const CORE_DOMAINS: CoreDomain[] = ["CONNECT", "OWN", "RESPOND", "ELEVATE"];
const WINDOW_DAYS = 30;
const HISTORY_DAYS = 90;
const PREVIEW_CONSTELLATION_STATES: BehaviorConstellationState[] = [
  {
    behaviorId: "preview-behavior-strategic-listening",
    behaviorName: PREVIEW_BEHAVIOR_NAME,
    theme: "CONNECT",
    supportScore: 0.78,
    oppositionScore: 0.22,
    netIntensity: 0.72,
    state: "mixed",
    supportingMeetingCount30d: 3,
    opposingMeetingCount30d: 1,
    lastSupportedAt: "2026-03-30T18:00:00.000Z",
    lastOpposedAt: "2026-03-28T18:00:00.000Z",
    evidenceMeetingIds: ["preview-meeting-roadmap", "preview-meeting-standup", "preview-meeting-stakeholder"],
    contextTags: ["preview", "meeting_evidence"],
    recentEvidence: [
      {
        meetingId: "preview-meeting-roadmap",
        meetingTitle: "Product Roadmap Sync",
        meetingStartTime: "2026-03-30T17:00:00.000Z",
        polarity: "supporting",
        sourceType: "transcript",
        excerpt: "Asked clarifying questions before offering a product recommendation.",
        confidence: "medium",
        contextTags: ["clarifying_questions"],
      },
      {
        meetingId: "preview-meeting-stakeholder",
        meetingTitle: "Stakeholder Update",
        meetingStartTime: "2026-03-28T17:00:00.000Z",
        polarity: "opposing",
        sourceType: "transcript",
        excerpt: "Moved quickly into next steps before checking for alignment.",
        confidence: "medium",
        contextTags: ["alignment_check"],
      },
    ],
    phase: "ACTIVE_PRACTICE",
  },
];

type ConstellationStateRow = {
  behavior_id: string;
  theme: CoreDomain;
  supporting_meeting_count_30d: number;
  opposing_meeting_count_30d: number;
  support_score: number;
  opposition_score: number;
  net_intensity: number;
  state: "dormant" | "emerging" | "strengthening" | "mixed" | "receding";
  last_supported_at: string | null;
  last_opposed_at: string | null;
  evidence_meeting_ids: string[] | null;
  context_tags: string[] | null;
};

type ObservationRow = {
  behavior_id: string;
  meeting_id: string;
  polarity: ObservationPolarity;
  source_type: "transcript" | "context_graph" | "behavioral_intelligence";
  confidence: "low" | "medium" | "high";
  evidence_excerpt: string | null;
  context_tags: string[] | null;
  observed_at: string;
  meetings?: { title: string | null; start_time: string | null } | null;
};

type LivingConstellationSource =
  | "persisted_constellation"
  | "fallback_practice_summary"
  | "degraded_missing_tables";

type LivingConstellationQueryResult = {
  states: BehaviorConstellationState[];
  source: LivingConstellationSource;
  governedBi?: GovernedBiContext;
};

export function useLivingConstellationState(leaderId?: string, ownerRouteOnly=false) {
  const { user } = useAuth();
  const targetLeaderId = leaderId || user?.id;

  const queryKey=["living-constellation-state", user?.id, targetLeaderId,...(ownerRouteOnly?["owner-route"]:[])];
  const { data, isLoading, error, refetch } = useQuery({
    staleTime:0,
    refetchInterval:ownerRouteOnly?60_000:false,
    refetchOnMount:"always",
    queryKey,
    enabled: !!targetLeaderId,
    queryFn: async (): Promise<LivingConstellationQueryResult> => {
      if (!targetLeaderId) {
        return {
          states: [],
          source: "fallback_practice_summary",
        };
      }
      if (targetLeaderId === PREVIEW_LEADER_USER_ID) {
        return {
          states: PREVIEW_CONSTELLATION_STATES,
          source: "persisted_constellation",
        };
      }

      const historyStart = new Date(Date.now() - HISTORY_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const windowStart = new Date(Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const typedSupabase = supabase as unknown as MinimalSupabaseClient;

      const audience=user?.id === targetLeaderId ? "leader" : "coach";
      const governedBi=await loadGovernedBiContext(supabase as unknown as BiRpcClient,targetLeaderId,audience);
      if(ownerRouteOnly||governedBi.ownerProjection)return {states:[],source:'persisted_constellation',governedBi};

      const {data:behaviorRows,error:behaviorError}=await supabase
        .from("behaviors").select("id, name, theme").eq("is_active",true)
        .order("theme",{ascending:true}).order("name",{ascending:true});
      if(behaviorError)throw behaviorError;
      const {data:leaderBehaviorRows,error:leaderBehaviorError}=await supabase
        .from("leader_behaviors").select("behavior_id, phase").eq("leader_id",targetLeaderId);
      if(leaderBehaviorError)throw leaderBehaviorError;

      const behaviors = (behaviorRows || [])
        .filter((row) => CORE_DOMAINS.includes(row.theme as CoreDomain))
        .map((row) => ({
          id: row.id,
          name: row.name,
          theme: row.theme as CoreDomain,
        })) as BehaviorRow[];

      const persistedLoad = await loadPersistedConstellationState({
        supabase: typedSupabase,
        leaderId: targetLeaderId,
        historyStart,
        audience,
        governedBi,
        behaviors,
      });

      if (persistedLoad.governedBi.ownerProjection) {
        return {states:[],source:'persisted_constellation',governedBi:persistedLoad.governedBi};
      }

      if (persistedLoad.degradedModes.missingConstellationTable) {
        emitSchemaFallbackTelemetry({
          objectName: "public.behavior_constellation_states",
          flow: "living_constellation_load",
          actorRole: "leader",
          route: "/insights",
          errorCode: "42P01",
          degradedMode: "derive_constellation_state_from_practice_summaries",
        });
      }

      if (persistedLoad.degradedModes.missingObservationTable) {
        emitSchemaFallbackTelemetry({
          objectName: "public.meeting_behavior_observations",
          flow: "living_constellation_load",
          actorRole: "leader",
          route: "/insights",
          errorCode: "42P01",
          degradedMode: "omit_persisted_constellation_evidence",
        });
      }

      const constellationStateRows = persistedLoad.constellationRows as ConstellationStateRow[];
      const observationRows = persistedLoad.observationRows as ObservationRow[];
      const phaseByBehaviorId = new Map(
        ((leaderBehaviorRows || []) as LeaderBehaviorRow[]).map((row) => [row.behavior_id, row.phase])
      );
      const stateByBehaviorId = new Map(constellationStateRows.map((row) => [row.behavior_id, row]));
      const observationsByBehaviorId = new Map<string, ObservationRow[]>();
      observationRows.forEach((row) => {
        const rows = observationsByBehaviorId.get(row.behavior_id) ?? [];
        rows.push(row);
        observationsByBehaviorId.set(row.behavior_id, rows);
      });

      if (constellationStateRows.length > 0) {
        const missingPersistedBehaviorIds = behaviors
          .filter((behavior) => !stateByBehaviorId.has(behavior.id))
          .map((behavior) => behavior.id);
        let fallbackStatesByBehaviorId = new Map<string, BehaviorConstellationState>();

        if (missingPersistedBehaviorIds.length > 0) {
          const { data: summaryRows, error: summaryError } = await supabase
            .from("practice_summaries")
            .select("behavior_id, meeting_id, meeting_start_time, execution_level, execution_confidence, behavior_insights, transcript_themes, meetings(title,start_time)")
            .eq("leader_id", targetLeaderId)
            .gte("meeting_start_time", historyStart)
            .order("meeting_start_time", { ascending: false });

          if (summaryError) console.warn('constellation_practice_overlay_read_failed');
          if (!summaryError) {
            fallbackStatesByBehaviorId = new Map(
              deriveConstellationStateFromPracticeSummaries({
                behaviors: behaviors.filter((behavior) => missingPersistedBehaviorIds.includes(behavior.id)),
                summaryRows: ((summaryRows || []) as unknown as PracticeSummaryRow[]).map(row=>({...row,behavior_insights:null,transcript_themes:null})),
                leaderBehaviorRows: (leaderBehaviorRows || []) as LeaderBehaviorRow[],
                historyStart,
                windowStart,
              }).map((state) => [state.behaviorId, state]),
            );
          }
        }

        return {
          states: behaviors.map((behavior) => {
            const persistedState = stateByBehaviorId.get(behavior.id);
            const persistedEvidenceRows = observationsByBehaviorId.get(behavior.id) ?? [];
            if (!persistedState) {
              return fallbackStatesByBehaviorId.get(behavior.id) ?? {
                behaviorId: behavior.id,
                behaviorName: behavior.name,
                theme: behavior.theme,
                supportScore: 0,
                oppositionScore: 0,
                netIntensity: 0,
                state: "dormant",
                supportingMeetingCount30d: 0,
                opposingMeetingCount30d: 0,
                lastSupportedAt: null,
                lastOpposedAt: null,
                evidenceMeetingIds: [],
                contextTags: [],
                recentEvidence: [],
                phase: phaseByBehaviorId.get(behavior.id) ?? null,
              } satisfies BehaviorConstellationState;
            }

            const recentEvidence = persistedEvidenceRows.slice(0, 6).map((row) => ({
              meetingId: row.meeting_id,
              meetingTitle: row.meetings?.title || "Meeting",
              meetingStartTime: row.meetings?.start_time || row.observed_at,
              polarity: row.polarity,
              sourceType: row.source_type,
              excerpt: row.evidence_excerpt,
              confidence: row.confidence,
              contextTags: Array.isArray(row.context_tags) ? row.context_tags.slice(0, 4) : [],
            })) satisfies ConstellationEvidenceItem[];

            return {
              behaviorId: behavior.id,
              behaviorName: behavior.name,
              theme: behavior.theme,
              supportScore: persistedState.support_score,
              oppositionScore: persistedState.opposition_score,
              netIntensity: persistedState.net_intensity,
              state: persistedState.state,
              supportingMeetingCount30d: persistedState.supporting_meeting_count_30d,
              opposingMeetingCount30d: persistedState.opposing_meeting_count_30d,
              lastSupportedAt: persistedState.last_supported_at,
              lastOpposedAt: persistedState.last_opposed_at,
              evidenceMeetingIds: persistedState.evidence_meeting_ids || [],
              contextTags: persistedState.context_tags || [],
              recentEvidence,
              phase: phaseByBehaviorId.get(behavior.id) ?? null,
            } satisfies BehaviorConstellationState;
          }),
          source: "persisted_constellation",
          governedBi: persistedLoad.governedBi,
        };
      }

      const { data: summaryRows, error: summaryError } = await supabase
        .from("practice_summaries")
        .select("behavior_id, meeting_id, meeting_start_time, execution_level, execution_confidence, behavior_insights, transcript_themes, meetings(title,start_time)")
        .eq("leader_id", targetLeaderId)
        .gte("meeting_start_time", historyStart)
        .order("meeting_start_time", { ascending: false });

      if (summaryError) throw summaryError;

      return {
        states: deriveConstellationStateFromPracticeSummaries({
          behaviors,
          summaryRows: ((summaryRows || []) as unknown as PracticeSummaryRow[]).map(row=>({...row,behavior_insights:null,transcript_themes:null})),
          leaderBehaviorRows: (leaderBehaviorRows || []) as LeaderBehaviorRow[],
          historyStart,
          windowStart,
        }),
        governedBi: persistedLoad.governedBi,
        source: persistedLoad.degradedModes.missingConstellationTable ? "degraded_missing_tables" : "fallback_practice_summary",
      };
    },
  });

  const isFetching = useIsFetching({ queryKey, exact: true }) > 0;
  const currentData = isFetching || error ? undefined : data;
  const groupedByTheme = useMemo(() => {
    const nodes = currentData?.states || [];
    return {
      CONNECT: nodes.filter((node) => node.theme === "CONNECT"),
      OWN: nodes.filter((node) => node.theme === "OWN"),
      RESPOND: nodes.filter((node) => node.theme === "RESPOND"),
      ELEVATE: nodes.filter((node) => node.theme === "ELEVATE"),
    };
  }, [currentData]);

  return {
    states: currentData?.states || [],
    groupedByTheme,
    source: currentData?.source ?? null,
    isLoading: isLoading || isFetching,
    error,
    refetch,
    governedBi: currentData?.governedBi ?? null,
    // Route choice only: cached evidence remains masked during refresh.
    isLegacyRoute:ownerRouteOnly&&!!data?.governedBi&&!data.governedBi.ownerProjection,
  };
}
